import {useAuth} from '@apps-common/auth-react';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import {Box, ImageListItemBar, styled} from '@mui/material';
import {Map, TileLayer, VectorTileLayer} from '@planet/layers';
import {array, func, number, object, string} from 'prop-types';
import React, {useEffect, useRef} from 'react';
import {getImageryURL} from '../util/collection.js';
import {toLocaleDateString} from '../util/date.js';
import {getMosaicsByDate} from '../util/map.js';

const StyledGridListTileBar = styled(ImageListItemBar)({
  '&': {
    textAlign: 'center',
    background:
      'linear-gradient(to top, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.3) 70%, rgba(0,0,0,0) 100%)',
  },
});

const MAPBOX_HEIGHT = 256;

const MapBox = styled(Box)(({theme, selected}) => ({
  // hehe
  borderRadius: theme.spacing(1),
  overflow: 'hidden',
  margin: theme.spacing(1),
  position: 'relative',
  width: '90%',
  height: MAPBOX_HEIGHT,
  backgroundColor: theme.palette.grey[600],
  border: `2px solid ${selected ? theme.vars.palette.primary.main : 'transparent'}`,
}));

/**
 * The observations grid shows thumbnails for all available time periods.
 * It shows a maximum of 5 at a time, and allows moving through the other time
 * periods with arrow buttons.
 * It is used by the ChangeViewer.
 */
const ObservationsGrid = ({
  compare,
  current,
  detectionsURL,
  featureStyle,
  interval,
  observations,
  onChange,
  view,
  mosaicsInfo,
}) => {
  const scrollRef = useRef();
  const auth = useAuth();

  useEffect(() => {
    const index = observations.indexOf(current);
    if (scrollRef.current) {
      // the + 16 handles the margins, if margins change this will
      //   need updated.
      scrollRef.current.parentNode.scrollTop = (MAPBOX_HEIGHT + 16) * index;
    }
  }, [current, observations]);

  return (
    <Box
      ref={scrollRef}
      sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {observations.slice().map(observation => (
        <MapBox
          key={observation.observed}
          onClick={
            current?.layer !== observation.layer
              ? onChange(observation)
              : undefined
          }
          selected={
            compare?.layer === observation.layer ||
            current?.layer === observation.layer
          }
          sx={{
            cursor:
              current?.layer !== observation.layer ? 'pointer' : 'default',
          }}
        >
          <Map
            style={{width: '100%', height: '100%'}}
            view={{extent: view.extent}}
          >
            <TileLayer
              url={getImageryURL(
                `Mosaic:${
                  getMosaicsByDate(
                    mosaicsInfo,
                    new Date(observation.observed),
                  )[0].name
                }`,
                auth.user?.accessToken,
              )}
            />
            {detectionsURL && (
              <VectorTileLayer layerStyle={featureStyle} url={detectionsURL} />
            )}
          </Map>
          <StyledGridListTileBar
            actionPosition="left"
            actionIcon={
              current?.layer === observation.layer ? (
                <LockOutlinedIcon />
              ) : undefined
            }
            title={toLocaleDateString(
              new Date(observation.observed),
              interval >= 1,
            )}
          />
        </MapBox>
      ))}
    </Box>
  );
};

ObservationsGrid.propTypes = {
  compare: object,
  current: object,
  detectionsURL: string,
  featureStyle: func,
  interval: number,
  observations: array,
  onChange: func,
  view: object,
  mosaicsInfo: array,
};

export default ObservationsGrid;
